import { GigsumoConstants } from 'src/app/services/GigsumoConstants';

/**
 * Which avatar the /notifications page shows in its left column for a
 * notification, so the header slider can show the same one:
 *  - a person's photo (`notifyData.logo`) or, failing that, their initials
 *    on a name-derived colour,
 *  - a business / community / network / message-group logo with a
 *    type-specific placeholder, or
 *  - a fixed icon (GigSumo face, workspace icon, …).
 *
 * `initials` carries the two name fields; the consumer derives the letters
 * and colours with JobService (getInitialsparam / getColorparam /
 * getTextColor) exactly as the page does.
 */
export type NotificationAvatar =
  | { kind: 'image'; src: string }
  | { kind: 'initials'; firstName: string; lastName: string };

const SUMOFACE = 'assets/icon/Sumoface_final.png';
const COMPANY = 'assets/images/gallery/company.png';
const COMMUNITY = 'assets/icon/comm.png';
const MESSAGE_GROUP = 'assets/icon/message.jpg';
const NETWORK = 'assets/icon/icons8-share-400.png';
const WORKSPACE = 'assets/icon/icons8-people-100.png';

/** Person: photo if present, else initials from firstName/lastName. */
const PERSON = new Set([
  'BUSINESS_FOLLOWER_ADDED',
  'POST_LIKED',
  'POST_COMMENTED',
  'POST_SHARED',
  'LEFT_FROM_MSG_GRP',
  'COMMUNITY_USER_INVITE_REQUEST_REJECTED',
  'COMMUNITY_USER_INVITE_REQUEST_ACCEPTED',
  'BUSINESS_FOLLOWER_INVITE_REQUEST_REJECTED',
  'BUSINESS_FOLLOWER_INVITE_REQUEST_ACCEPTED',
  'COMMUNITY_USER_JOIN_REQUEST',
  'CONNECT_REQUEST_RECEIVED',
  'CONNECTION_REMOVED',
  'JOINED_NETWORK',
  'User_itself_remove_from_Network',
  'Network_Removed',
  'NETWORK_INVITE_SENT',
  'NETWORK_CONNECT_REQUEST_REJECTED',
  'NETWORK_CONNECT_REQUEST_ACCEPTED',
  'JOINED_TEAM',
  GigsumoConstants.LEFT_TEAM,
  'TEAMS_REMOVED',
  'CONNECT_REQUEST_CANCELLED',
  'TEAM_CONNECT_REQUEST_CANCELLED',
  'BUSINESS_EMPLOYEE_INVITE_REQUEST',
  'CANDIDATE_INVITATION_REJECTED',
  'CANDIDATE_INVITATION_ACCEPTED',
  'CANDIDATE_ONBOARDED',
  'CANDIDATE_REJECTED',
  'CANDIDATE_PREONBOARDED',
  'JOB-OFFERED',
  'OFFER-WITHDRAWN',
  'CANDIDATE BACKOUT',
  'CANDIDATE-SHORTLISTED',
  'INTERVIEW-SCHEDULED',
  'CANDIDATE_GOT_SELECTED',
  'JOB_STATUS_CHANGED',
  'RESUME_REQUEST_ACCEPTED',
  'RESUME_UPDATED',
  'RESUME_REQUEST_INITIATED',
]);

/** Job-owner person: photo if present, else initials from jobOwner* fields. */
const JOB_OWNER = new Set([
  'JOB_OWNER_REQUEST',
  'JOB_OWNER_ADDITIONAL_REQUEST',
  'CANDIDATE_DOCUMENT_REJECTION',
  'CANDIDATE_DOCUMENT_EXPIRED',
  'CANDIDATE_DOCUMENT_ALL_VERIFIED',
]);

/** Candidate-owner person: photo if present, else initials from CandidateOwner* fields. */
const CANDIDATE_OWNER = new Set([
  'CANDIDATE_OWNER_SUBMISSION',
  'CANDIDATE_DOCUMENT_REUPLOAD',
  'CANDIDATE_EXPIRED_DOCUMENT_REUPLOAD',
]);

/** Business logo, else the company placeholder. */
const BUSINESS = new Set([
  'BUSINESS_EMPLOYEE_REQUEST_REJECTED',
  'BUSINESS_EMPLOYEE_REQUEST_ACCEPTED',
  'BUSINESS_PAGE_DEACTIVATED',
  'BUSINESS_PAGE_ACTIVATED',
  'JOB_SOURCE_TRANSFER',
  'JOB_TARGET_TRANSFER',
  'CANDIDATE_SOURCE_TRANSFER',
  'CANDIDATE_TARGET_TRANSFER',
  'PLAN_ACCESS_REVOKED_SOURCE_TRANSFER',
  'PLAN_ACCESS_REVOKED_JOB_TARGET_RECEIVE',
  'PLAN_ACCESS_REVOKED_CANDIDATE_TARGET_RECEIVE',
  'PLAN_ACCESS_REVOKED_WORKSPACE_TARGET_RECEIVE',
  'BUSINESS_EMPLOYEE_REVOKE',
  'BUSINESS_EMPLOYEE_ADDED',
  'BUSINESS_PAGE_FOLLOWER_INVITE_SENT',
  'BUSINESS_PAGE_CONTACT_INVITE',
  'BUSINESS_COLLABRATION_INVITE',
  GigsumoConstants.TEAM_DEACTIVATED,
]);

/** Contract notifications (generic block): businessLogo || logo, else company placeholder. */
const CONTRACT = new Set([
  'CONTRACT_AGREEMENT_DOCUMENT_UPLOADED',
  'CONTRACT_COMPANY_DOC_CHECKLIST_CREATED',
  'CONTRACT_COMPANY_DOC_UPLOADED',
  'CONTRACT_COMPANY_DOC_VERIFIED',
  'CONTRACT_COMPANY_DOC_REJECTED',
  'CONTRACT_AGREEMENT_SIGNING_REQUESTED',
  'CONTRACT_AGREEMENT_SIGNED',
  'CONTRACT_AGREEMENT_ACCEPTED',
  'CONTRACT_AGREEMENT_REJECTED',
  'CONTRACT_WORK_ORDER_INITIATED',
  'CONTRACT_WORK_ORDER_ACCEPTED',
  'CONTRACT_WORK_ORDER_REJECTED',
  'CONTRACT_WORK_ORDER_REVISED',
  'CONTRACT_WORK_ORDER_VOIDED',
  'CONTRACT_INITIATED',
  'CONTRACT_INVOICE_SUBMITTED',
  'CONTRACT_INVOICE_UPDATED',
  'CONTRACT_INVOICE_APPROVED',
  'CONTRACT_INVOICE_REJECTED',
  'CONTRACT_INVOICE_PAID',
  'CONTRACT_PAYMENT_RECEIVED',
  'CONTRACT_INVOICE_VOIDED',
]);

/** Community logo, else the community placeholder. */
const COMMUNITY_LOGO = new Set([
  'COMMUNITY_PAGE_DEACTIVATED',
  'COMMUNITY_PAGE_ACTIVATED',
  'COMMUNITY_MEMBER_REQUEST_ACCEPTED',
  'COMMUNITY_MEMBER_REQUEST_REJECTED',
  'COMMUNITY_USER_REQUEST_REJECTED',
  'COMMUNITY_USER_REQUEST_ACCEPTED',
  'COMMUNITY_MEMBER_JOINED',
]);

const MESSAGE_GROUP_LOGO = new Set(['REMOVED_FROM_MSG_GRP', 'ADDED_TO_MSG_GRP']);

const NETWORK_LOGO = new Set(['NETWORK_DELETED', 'NETWORK_ACTIVATED', 'NETWORK_INVITE_CANCELLED']);

/** Always the workspace icon, logo or not. */
const WORKSPACE_ICON = new Set([
  GigsumoConstants.TEAM_INACTIVE,
  GigsumoConstants.LEFT_TEAM_BY_ORG_CHANGE,
  GigsumoConstants.SUPERADMIN_INACTIVE,
  'TEAM_ACTIVATED',
  'TEAM_OWNER_UPDATE',
  'TEAM_INVITE_CANCELLED',
]);

function hasLogo(d: any): boolean {
  const v = d?.logo;
  return v != null && v !== '' && v !== 'null' && v !== 'undefined';
}

export function notificationAvatar(item: any): NotificationAvatar {
  const type = String(item?.notifyType || '');
  const d = item?.notifyData || {};
  const image = (src: string): NotificationAvatar => ({ kind: 'image', src });
  const initials = (firstName: any, lastName: any): NotificationAvatar =>
    ({ kind: 'initials', firstName: firstName || '', lastName: lastName || '' });

  if (PERSON.has(type)) return hasLogo(d) ? image(d.logo) : initials(d.firstName, d.lastName);
  if (JOB_OWNER.has(type)) return hasLogo(d) ? image(d.logo) : initials(d.jobOwnerFirstName, d.jobOwnerLastName);
  if (CANDIDATE_OWNER.has(type)) return hasLogo(d) ? image(d.logo) : initials(d.CandidateOwnerFirstName, d.CandidateOwnerLastName);
  // Page quirk kept as is: initials when no logo, workspace icon when there is one.
  if (type === 'TEAM_CONNECT_REQUEST_ACCEPTED') return hasLogo(d) ? image(WORKSPACE) : initials(d.firstName, d.lastName);

  if (BUSINESS.has(type)) return image(hasLogo(d) ? d.logo : (d.businessLogo || COMPANY));
  if (type === 'BUSINESS_EMPLOYEE_INVITE_SENT') return image(hasLogo(d) ? d.logo : COMPANY);
  if (CONTRACT.has(type)) return image(d.businessLogo || (hasLogo(d) ? d.logo : COMPANY));

  if (COMMUNITY_LOGO.has(type)) return image(hasLogo(d) ? d.logo : COMMUNITY);
  if (type === 'COMMUNITY_USER_INVITE_REQUEST') return image(COMMUNITY);
  if (MESSAGE_GROUP_LOGO.has(type)) return image(hasLogo(d) ? d.logo : MESSAGE_GROUP);
  if (NETWORK_LOGO.has(type)) return image(hasLogo(d) ? d.logo : NETWORK);
  if (WORKSPACE_ICON.has(type)) return image(WORKSPACE);

  if (type === 'INSUFFICIENT_CREDITS_ALERT') return image(hasLogo(d) ? 'assets/images/gigsumo-admin.jpg' : 'assets/images/userAvatar.png');

  // Platform notifications (credits, Stripe, admin actions, plan changes,
  // accepted contact invites, COMMUNITY_USER_JOINED, …) show the GigSumo face.
  return image(SUMOFACE);
}
